import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  InfoOutlined,
  Add,
  CloseOutlined
} from "@mui/icons-material";
import { Typography } from "@mui/material";

import Upload from "../../../components/Upload";
import Input from "../../../components/Input";
import Select from "../../../components/Select";
import Button from "../../../components/Button";
import Tooltip from "../../../components/Tooltip";
import messages from "../../../assets/locale/messages";
import { categoriesLookupRequest } from "../../../store/Lookups/actions";
import {
  addSubCategoryRequest,
  editSubCategoryRequest,
  getSubCategoryDetailsRequest,
  getSubCategoryDetailsResponse
} from "../../../store/SubCategories/actions";
import {
  BLOB_CONTAINERS,
  UploadBlobToContainer
} from "../../../utils/blobStorageServices";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import "./AddEditSubCategory.scss";

const AddEditSubCategory = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const uploadElementRef = useRef();
  const specificationFieldArrayRef = useRef();

  const lang = useSelector((state) => state.locale.lang);
  const subCategoryDetails = useSelector(
    (state) => state.subCategories.subCategoryDetails
  );
  const categoriesLookup = useSelector(
    (state) => state.lookups.categories.list
  );
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const { shared, subCategories, inputsValidations } = messages[lang];

  useEffect(() => {
    dispatch(categoriesLookupRequest());
    return () => {
      dispatch(getSubCategoryDetailsResponse({}));
    };
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(
        getSubCategoryDetailsRequest({
          id
        })
      );
    }
  }, [id]);

  useEffect(() => {
    if (categoriesLookup) {
      setCategoriesOptions([
        ...categoriesLookup.map(({ Id, Name }) => {
          return {
            id: Id,
            label: Name
          };
        })
      ]);
    }
  }, [categoriesLookup]);

  Yup.addMethod(
    Yup.array,
    "unique",
    function (message, mapper = (a) => a) {
      return this.test("unique", message, function (list) {
        if (list) {
          const seen = {};
          const duplicates = {};

          // First pass to identify all duplicates
          list.forEach((item, index) => {
            const value = mapper(item)?.trim()?.toLowerCase();
            if (seen[value]) {
              duplicates[value] = true;
            } else {
              seen[value] = true;
            }
          });

          // Second pass to collect errors for all duplicates
          const errors = list
            .map((item, index) => {
              const value = mapper(item)?.trim()?.toLowerCase();
              if (duplicates[value]) {
                return this.createError({
                  path: `${this.path}[${index}].Name`,
                  message: message
                });
              }
              return null;
            })
            .filter((error) => error !== null);

          if (errors.length > 0) {
            return new Yup.ValidationError(errors);
          }
        }
        return true;
      });
    }
  );

  const formik = useFormik({
    initialValues: {
      Name: "",
      Description: "",
      CategoryId: null,
      AdminCoverImage: "",
      AdminCoverImageInfo: {},
      WebsiteCoverImage: "",
      WebsiteCoverImageInfo: {},
      SubCategorySpecifications: [
        {
          Name: ""
        }
      ]
    },
    validationSchema: Yup.object({
      Name: Yup.string()
        .max(50, "subCategoryNameLong")
        .required("subCategoryNameRequired"),
      Description: Yup.string()
        .max(500, "subCategoryDescriptionLong")
        .required("subCategoryDescriptionRequired"),
      CategoryId: Yup.object().required(
        "subCategoryParentCategoryRequired"
      ),
      AdminCoverImage: Yup.mixed().required(
        "subCategoryImageRequired"
      ),
      WebsiteCoverImage: Yup.mixed().required(
        "subCategoryCoverImageRequired"
      ),
      SubCategorySpecifications: Yup.array()
        .of(
          Yup.object().shape({
            Name: Yup.string()
              .max(30, "subCategorySpecificationsMaxLength")
              .required(
                "subCategorySpecificationsSuggestionsRequired"
              )
          })
        )
        .unique(
          "subCategorySpecificationsUnique",
          (item) => item.Name
        )
    }),
    onSubmit: async ({
      Name,
      Description,
      CategoryId,
      AdminCoverImage,
      AdminCoverImageBE,
      AdminCoverImageInfo,
      WebsiteCoverImage,
      WebsiteCoverImageBE,
      WebsiteCoverImageInfo,
      SubCategorySpecifications
    }) => {
      let adminFileName = "";
      if (AdminCoverImage) {
        if (AdminCoverImageInfo.isNew) {
          adminFileName = await UploadBlobToContainer(
            { blob: AdminCoverImage, ...AdminCoverImageInfo },
            BLOB_CONTAINERS.images
          );
        } else {
          adminFileName = AdminCoverImage.split("/images/")[1];
        }
      }
      let websiteFileName = "";
      if (WebsiteCoverImage) {
        if (WebsiteCoverImageInfo.isNew) {
          websiteFileName = await UploadBlobToContainer(
            { blob: WebsiteCoverImage, ...WebsiteCoverImageInfo },
            BLOB_CONTAINERS.images
          );
        } else {
          websiteFileName = WebsiteCoverImage.split("/images/")[1];
        }
      }

      const subCategoryData = {
        Id: 0,
        Name,
        Description,
        CategoryId: CategoryId.id,
        AdminCoverImage: adminFileName,
        OriginalAdminCoverImageName: AdminCoverImageInfo.name,
        WebsiteCoverImage: websiteFileName,
        OriginalWebsiteCoverImageName: WebsiteCoverImageInfo.name,
        SubCategorySpecifications,
        isAdminImageNew: AdminCoverImageInfo.isNew,
        AdminCoverImageBE,
        isWebsiteImageNew: WebsiteCoverImageInfo.isNew,
        WebsiteCoverImageBE
      };

      if (id) {
        subCategoryData.Id = id;
        dispatch(
          editSubCategoryRequest({
            data: subCategoryData,
            navigate,
            id
          })
        );
      } else {
        dispatch(
          addSubCategoryRequest({
            data: subCategoryData,
            navigate
          })
        );
      }
    }
  });

  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleSubmit,
    dirty,
    isValid,
    setValues
  } = formik;

  useEffect(() => {
    if (
      subCategoryDetails &&
      Object.keys(subCategoryDetails).length
    ) {
      const {
        Name,
        Description,
        CategoryId,
        CategoryName,
        AdminCoverImage,
        OriginalAdminCoverImageName,
        WebsiteCoverImage,
        OriginalWebsiteCoverImageName,
        Specification
      } = subCategoryDetails;

      setValues({
        Name,
        Description,
        CategoryId: { id: CategoryId, label: CategoryName },
        AdminCoverImage,
        AdminCoverImageBE: AdminCoverImage,
        AdminCoverImageInfo: {
          isNew: false,
          name: OriginalAdminCoverImageName
        },
        WebsiteCoverImage,
        WebsiteCoverImageBE: WebsiteCoverImage,
        WebsiteCoverImageInfo: {
          isNew: false,
          name: OriginalWebsiteCoverImageName
        },
        SubCategorySpecifications: [...Specification]
      });
    }
  }, [subCategoryDetails]);

  return (
    <div className="add-subcategory-container">
      <div className="page-title">
        <h3 className="fweight-700 fsize-22 mb-4 title-color">
          {id
            ? subCategories.addSubcategory.editSubCategory
            : subCategories.addSubcategory.addSubCategory}
        </h3>
      </div>
      <div className="add-edit-sub-category-form ">
        <FormikProvider value={formik}>
          <form noValidate onSubmit={handleSubmit}>
            <div className="main-data-section bg-white border-radius-10 p-4 pb-5 mb-4">
              <h5 className="fsize-16 fweight-600 mb-4 title-color">
                {subCategories.addSubcategory.subCategoryInfo}
              </h5>
              <div className={`row`}>
                <div className="cover-image img-container col-12 mb-5 mb-md-4">
                  <div className="title d-flex align-items-center justify-content-start mb-3">
                    <h6 className="text-light-grey mb-0 me-2 ">
                      {
                        subCategories.addSubcategory.labels
                          .websiteImage
                      }
                    </h6>
                    <Tooltip
                      className="text-secondary ms-md-2"
                      title={
                        <ul className="mb-0 ps-0">
                          <li>{inputsValidations.uploadSizeError}</li>
                          <li>
                            {inputsValidations.uploadImgSizeError}
                          </li>
                          <li>{inputsValidations.uploadTypeError}</li>
                        </ul>
                      }
                      content={
                        <InfoOutlined className="text-secondary" />
                      }
                    />
                  </div>
                  <Upload
                    required
                    label={<div ref={uploadElementRef}></div>}
                    onChange={(img) => {
                      setFieldTouched("WebsiteCoverImage");
                      setValues({
                        ...values,
                        WebsiteCoverImage: img.blob,
                        WebsiteCoverImageInfo: {
                          ...img?.imageInfo,
                          isNew: true
                        }
                      });
                    }}
                    onClear={() => {
                      setFieldTouched("WebsiteCoverImage");
                      setValues({
                        ...values,
                        WebsiteCoverImage: null,
                        WebsiteCoverImageInfo: {}
                      });
                    }}
                    id="WebsiteCoverImage"
                    name="WebsiteCoverImage"
                    userImage={values.WebsiteCoverImage}
                    isInputHasErr={
                      !!(
                        touched.WebsiteCoverImage &&
                        errors.WebsiteCoverImage
                      )
                    }
                    errMsg={errors.WebsiteCoverImage}
                    showValidationMsg
                    isCropMode={true}
                    cropAspect={8.25 / 1}
                  />
                </div>
                <div className="col-12 mt-5 mb-3">
                  <div className="row mb-3">
                    <div className="admin-image img-container col-xl-4 col-md-5 col-12 mb-5 mb-md-0 ">
                      <div className="title d-flex align-items-center justify-content-start mb-3">
                        <h6 className="text-light-grey mb-0 me-2 ">
                          {
                            subCategories.addSubcategory.labels
                              .adminImage
                          }
                        </h6>
                        <Tooltip
                          className="text-secondary ms-md-2"
                          title={
                            <ul className="mb-0 ps-0">
                              <li>
                                {inputsValidations.uploadSizeError}
                              </li>
                              <li>
                                {inputsValidations.uploadImgSizeError}
                              </li>
                              <li>
                                {inputsValidations.uploadTypeError}
                              </li>
                            </ul>
                          }
                          content={
                            <InfoOutlined className="text-secondary" />
                          }
                        />
                      </div>
                      <Upload
                        required
                        label={<div ref={uploadElementRef}></div>}
                        onChange={(img) => {
                          setFieldTouched("AdminCoverImage");
                          setValues({
                            ...values,
                            AdminCoverImage: img.blob,
                            AdminCoverImageInfo: {
                              ...img?.imageInfo,
                              isNew: true
                            }
                          });
                        }}
                        onClear={() => {
                          setFieldTouched("AdminCoverImage");
                          setValues({
                            ...values,
                            AdminCoverImage: null,
                            AdminCoverImageInfo: {}
                          });
                        }}
                        id="AdminCoverImage"
                        name="AdminCoverImage"
                        userImage={values.AdminCoverImage}
                        isInputHasErr={
                          !!(
                            touched.AdminCoverImage &&
                            errors.AdminCoverImage
                          )
                        }
                        errMsg={errors.AdminCoverImage}
                        showValidationMsg
                        isCropMode={true}
                        cropAspect={1 / 1}
                      />
                    </div>
                    <div className="col-xl-8 col-md-7 col-12 mt-md-0 mt-4">
                      <h6 className="text-light-grey opacity-0 mb-3">
                        {
                          subCategories.addSubcategory.labels
                            .adminImage
                        }
                      </h6>
                      <div>
                        <Select
                          required
                          wrapperClass="mb-4"
                          labelClassName=""
                          label={
                            subCategories.addSubcategory.labels
                              .category
                          }
                          placeholder={
                            subCategories.addSubcategory.placeholders
                              .category
                          }
                          options={categoriesOptions}
                          onChange={(value) => {
                            setFieldTouched("CategoryId");
                            setFieldValue("CategoryId", value);
                          }}
                          value={values.CategoryId}
                          hasError={
                            !!(
                              touched.CategoryId && errors.CategoryId
                            )
                          }
                          errMsg={errors.CategoryId}
                          id="CategoryId"
                          name="CategoryId"
                        />
                        <Input
                          required
                          inputWrapperClass="mb-4"
                          label={
                            subCategories.addSubcategory.labels
                              .subCategoryName
                          }
                          placeholder={
                            subCategories.addSubcategory.placeholders
                              .subCategoryName
                          }
                          onChange={(value) => {
                            if (!value || value.trim()) {
                              const modifiedValue = value.replace(
                                /\s\s+/g,
                                " "
                              );
                              if (value[0] !== " ") {
                                setFieldTouched("Name");
                                setFieldValue("Name", modifiedValue);
                              }
                            }
                          }}
                          value={values.Name}
                          isInputHasErr={
                            !!(touched.Name && errors.Name)
                          }
                          errMsg={errors.Name}
                          name="Name"
                          id="Name"
                        />
                        <Input
                          required
                          label={
                            subCategories.addSubcategory.labels
                              .subCategoryDescription
                          }
                          placeholder={
                            subCategories.addSubcategory.placeholders
                              .subCategoryDescription
                          }
                          onChange={(value) => {
                            if (!value || value.trim()) {
                              const modifiedValue = value.replace(
                                /\s\s+/g,
                                " "
                              );
                              if (value[0] !== " ") {
                                setFieldTouched("Description");
                                setFieldValue(
                                  "Description",
                                  modifiedValue
                                );
                              }
                            }
                          }}
                          value={values.Description}
                          isInputHasErr={
                            !!(
                              touched.Description &&
                              errors.Description
                            )
                          }
                          errMsg={errors.Description}
                          name="Description"
                          id="Description"
                          rows={4}
                          multiline
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="specs-data-section bg-white border-radius-10 p-4 mb-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="title d-flex align-items-sm-center justify-content-start">
                  <Typography
                    className="fsize-16 fweight-600 mb-0 me-2 title-color"
                    sx={{
                      width: {
                        xs: "min-content",
                        sm: "auto"
                      }
                    }}>
                    {
                      subCategories.addSubcategory
                        .specificationsSuggestions
                    }
                  </Typography>
                  <Tooltip
                    className="text-secondary ms-md-2"
                    title={
                      inputsValidations.subCategorySpecificationsMaxNumber
                    }
                    content={
                      <InfoOutlined className="text-secondary" />
                    }
                  />
                </div>
                <Button
                  outlined
                  label={
                    <span className="d-flex justify-content-center align-items-center">
                      <Add className="add-icon" fontSize="medium" />
                      <span className="add-more-text ps-2 d-none d-md-block">
                        {subCategories.addSubcategory.addMore}
                      </span>
                    </span>
                  }
                  disabled={
                    values.SubCategorySpecifications?.length >= 10
                  }
                  onClick={() => {
                    if (values.SubCategorySpecifications.length < 10)
                      specificationFieldArrayRef.current.push({
                        Name: ""
                      });
                  }}
                  customSpacing={"px-2 px-md-4"}
                />
              </div>
              <div className="suggestions-container row">
                <div className="col-12 col-md-8 col-lg-7">
                  <FieldArray
                    name="SubCategorySpecifications"
                    render={(arrayHelpers) => {
                      specificationFieldArrayRef.current =
                        arrayHelpers;
                      return values.SubCategorySpecifications?.map(
                        (_, index) => (
                          <div
                            key={`spec-suggestion-${index}`}
                            className="mb-4 row">
                            <div className="col-md-10 col-9">
                              <Input
                                required
                                label={
                                  subCategories.addSubcategory.labels
                                    .speicificationName
                                }
                                placeholder={
                                  subCategories.addSubcategory
                                    .placeholders.speicificationName
                                }
                                onChange={(value) => {
                                  if (!value || value.trim()) {
                                    const modifiedValue =
                                      value.replace(/\s\s+/g, " ");
                                    if (value[0] !== " ") {
                                      setFieldTouched(
                                        `SubCategorySpecifications[${index}].Name`
                                      );
                                      setFieldValue(
                                        `SubCategorySpecifications[${index}].Name`,
                                        modifiedValue
                                      );
                                    }
                                  }
                                }}
                                value={
                                  values.SubCategorySpecifications[
                                    index
                                  ].Name
                                }
                                isInputHasErr={
                                  !!(
                                    touched
                                      ?.SubCategorySpecifications?.[
                                      index
                                    ]?.Name &&
                                    errors
                                      ?.SubCategorySpecifications?.[
                                      index
                                    ]?.Name
                                  )
                                }
                                errMsg={
                                  errors?.SubCategorySpecifications?.[
                                    index
                                  ]?.Name
                                }
                                name={`Name-${index}`}
                                id={`Name-${index}`}
                              />
                            </div>
                            <div className="action-btns col-md-2 col-3 d-flex  align-items-center">
                              {values.SubCategorySpecifications
                                ?.length > 1 && (
                                <span
                                  className="pointer p-1 circular-border-radius grey-bg d-flex align-items-center justify-content-center"
                                  onClick={() => {
                                    specificationFieldArrayRef.current.remove(
                                      index
                                    );
                                  }}>
                                  <CloseOutlined fontSize="small" />
                                </span>
                              )}
                              {values.SubCategorySpecifications
                                ?.length < 10 &&
                                values.SubCategorySpecifications
                                  ?.length -
                                  1 ===
                                  index && (
                                  <span
                                    className="pointer p-1 circular-border-radius grey-bg d-flex align-items-center justify-content-center ms-4"
                                    onClick={() => {
                                      if (
                                        values
                                          .SubCategorySpecifications
                                          .length < 10
                                      )
                                        specificationFieldArrayRef.current.push(
                                          {
                                            Name: ""
                                          }
                                        );
                                    }}>
                                    <Add fontSize="small" />
                                  </span>
                                )}
                            </div>
                          </div>
                        )
                      );
                    }}
                  />
                </div>
                {values.SubCategorySpecifications?.length >= 10 && (
                  <div className="col-12">
                    <p className="text-secondary mb-0">
                      {
                        inputsValidations.subCategorySpecificationsMaxNumber
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="actions-section bg-white border-radius-10 p-4 d-flex justify-content-between align-items-center">
              <Button
                key={shared.cancel}
                outlined
                label={shared.cancel}
                onClick={() => navigate(ROUTE_PATHS.subCategories)}
                customSpacing={"px-4 px-md-5"}
              />
              <Button
                label={id ? shared.save : shared.submit}
                type="submit"
                disabled={!dirty || !isValid}
                customSpacing={"px-4 px-md-5"}
              />
            </div>
          </form>
        </FormikProvider>
      </div>
    </div>
  );
};

export default AddEditSubCategory;
